'use client';

import React from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import { Drawer } from '@depop/web-ui-kit/Drawer';
import { Modal } from '@depop/web-ui-kit/Modal';

import styles from './styles.module.css';
import { useSheetDragToDismiss } from './useSheetDragToDismiss';

import { DownloadAppButton } from '@/components/DownloadAppButton';
import { DownloadAppQRCodeSection } from '@/components/DownloadAppQRCodeSection';
import { type AppDownloadPlacement } from '@/modules/branch/placements';

type Props = {
  id: string;
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  title: string;
  description: string;
  illustration: React.ReactNode;
  trackingId: string;
  isSheet?: boolean;
  placement?: AppDownloadPlacement;
};

export function AppUpsellModal({
  id,
  isOpen,
  setOpen,
  title,
  description,
  illustration,
  trackingId,
  isSheet,
  placement = 'app_upsell_modal',
}: Props) {
  const { handleRef, dragHandlers } = useSheetDragToDismiss(() =>
    setOpen(false)
  );

  function renderContent() {
    return (
      <div className={styles.container}>
        <Heading
          variant={isSheet ? 'lg' : 'md'}
          as="h2"
          className={styles.title}
        >
          {title}
        </Heading>
        <Body className={styles.description}>{description}</Body>
        <div className={styles.illustration}>{illustration}</div>
        <div className={styles.downloadButton}>
          <DownloadAppButton trackingId={trackingId} block />
        </div>
        <div className={styles.qrCode}>
          <DownloadAppQRCodeSection
            placement={placement}
            sourceId={trackingId}
          />
        </div>
      </div>
    );
  }

  if (isSheet) {
    return (
      <Drawer
        position="bottom"
        isPartialBottomDrawer
        isOpen={isOpen}
        setOpen={setOpen}
        label={title}
        className={styles.sheet}
        customOverrides={{
          header: (
            <span
              ref={handleRef}
              className={styles.grabber}
              aria-hidden="true"
              {...dragHandlers}
            />
          ),
        }}
        content={renderContent}
      />
    );
  }

  return (
    <Modal
      id={id}
      isOpen={isOpen}
      setOpen={setOpen}
      label={title}
      overlayAriaLabel={title}
      className={styles.modal}
      customOverrides={{
        fullscreenAtSmallBreakpoint: true,
      }}
      content={renderContent}
    />
  );
}
